import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastCtx {
  toasts: Toast[];
  show: (message: string, type: ToastType, duration?: number) => string;
  hide: (id: string) => void;
  success: (message: string, duration?: number) => string;
  error: (message: string, duration?: number) => string;
  info: (message: string, duration?: number) => string;
  warning: (message: string, duration?: number) => string;
}

const Ctx = createContext<ToastCtx | null>(null);

const DEFAULT_DURATION = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback((message: string, type: ToastType, duration = DEFAULT_DURATION) => {
    const id = Math.random().toString(36).slice(2, 9);
    setToasts((prev) => [...prev, { id, message, type, duration }]);
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
    return id;
  }, []);

  const hide = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const success = useCallback((message: string, duration?: number) => show(message, 'success', duration), [show]);
  const error = useCallback((message: string, duration?: number) => show(message, 'error', duration), [show]);
  const info = useCallback((message: string, duration?: number) => show(message, 'info', duration), [show]);
  const warning = useCallback((message: string, duration?: number) => show(message, 'warning', duration), [show]);

  return (
    <Ctx.Provider value={{ toasts, show, hide, success, error, info, warning }}>
      {children}
      <ToastContainer toasts={toasts} onHide={hide} />
    </Ctx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

function ToastContainer({ toasts, onHide }: { toasts: Toast[]; onHide: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onHide={onHide} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onHide }: { toast: Toast; onHide: (id: string) => void }) {
  const bg = {
    success: 'bg-plug text-white',
    error: 'bg-wine text-white',
    info: 'bg-void text-bone',
    warning: 'bg-gold text-bone',
  }[toast.type];

  const icon = {
    success: <CheckIcon />,
    error: <XIcon />,
    info: <InfoIcon />,
    warning: <AlertIcon />,
  }[toast.type];

  return (
    <div
      className={`pointer-events-auto flex items-center gap-3 rounded-xl px-4 py-3 shadow-lift min-w-[280px] max-w-md animate-slide-in ${bg}`}
      role="alert"
      aria-live="polite"
    >
      <span className="flex-shrink-0">{icon}</span>
      <p className="text-[14px] font-medium flex-1">{toast.message}</p>
      <button
        type="button"
        onClick={() => onHide(toast.id)}
        className="flex-shrink-0 p-1 rounded hover:bg-black/10 text-current opacity-70 hover:opacity-100"
        aria-label="Dismiss"
      >
        <CloseIcon />
      </button>
    </div>
  );
}

function CheckIcon() {
  return <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden><path d="M3 9l4 4 8-8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}
function XIcon() {
  return <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden><path d="M4 4l10 10M14 4l-10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>;
}
function InfoIcon() {
  return <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden><circle cx="9" cy="9" r="7.5" fill="none" stroke="currentColor" strokeWidth="1.8"/><path d="M9 5v5M9 12v.01" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>;
}
function AlertIcon() {
  return <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden><path d="M9 2l7 13H2z" fill="none" stroke="currentColor" strokeWidth="1.8"/><path d="M9 6v4M9 12v.01" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>;
}
function CloseIcon() {
  return <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden><path d="M3 3l8 8M11 3L3 11" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>;
}